/**
 * post-V9 Section 0 Iter 3 — compact stores for annotation primitives:
 * Dimension and Multileader.
 *
 * Dimension keeps the subtype tagged (group 70 dimensionType) so one
 * compact store covers all 6 subtypes. Multileader serializes
 * leaderRoots to JSON because the 4-level nesting (root.nodes.lines.
 * vertices) is too complex for a flat offset/length encoding in this
 * proof-of-concept iteration.
 */
import type {
  AnnotativeContext,
  DimensionEntity,
  DrawingPoint3D,
  MultileaderEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import {
  internString,
  lookupString,
  type SharedStringTable,
} from "./sharedStringTable";

/* ── DIMENSION ── */

const DIM_FLOATS = 10;
const DIM_FLOATS_2 = 2;
const DIM_INTS = 4;

export interface CompactDimensionStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  floats2: Float32Array;
  ints: Uint16Array;
  types: Uint8Array;
  extLine1Points: Map<number, { x: number; y: number }>;
  extLine2Points: Map<number, { x: number; y: number }>;
  thirdPoints: Map<number, DrawingPoint3D>;
  arcPoints: Map<number, DrawingPoint3D>;
  annotativeContexts: Map<number, AnnotativeContext[]>;
  // S-DIMENSION-EXTRAS — sparse optional fields kept as Maps (mirrors the
  // optional-point Maps above + the RevCloud thickness/extrusion Maps) so the
  // fixed typed-array layout stays unchanged for the common default dimension.
  versions: Map<number, number>; // code 280
  lineSpacingStyles: Map<number, number>; // code 72
  lineSpacingFactors: Map<number, number>; // code 41
  insertionPoints: Map<number, DrawingPoint3D>; // code 12/22/32
  thicknesses: Map<number, number>; // code 39
  extrusions: Map<number, DrawingPoint3D>; // code 210/220/230
  // GitHub Codex review PR #558 round 2 P1 — MIRRTEXT glyph-handedness
  // (`DimensionEntity.textMirrored`'s doc: render-only, no DXF group, so no
  // presence-vs-false byte-fidelity concern — same collapse convention
  // Multileader's own `FLAG_TEXT_MIRRORED` bit already uses). Sparse `Set`
  // (membership ⇒ true) rather than a new fixed flags byte column: this
  // store has no existing bitfield to extend (unlike `CompactMultileaderStore
  // .flags`), and every OTHER rare-optional field here (`versions`,
  // `lineSpacingStyles`, …) already uses the identical sparse-Map/Set idiom.
  // Without this, `appendDimension` (the PRIMARY entity write-through path,
  // populated on every import/mutation) silently dropped a MIRRTEXT=1
  // mirror's `textMirrored: true`, so `getEntityById`/read-through
  // snapshots reconstructed an unmirrored glyph the instant the compact
  // cache round-tripped the entity.
  textMirrored: Set<number>;
}

export function createCompactDimensionStore(
  initialCapacity = 256,
): CompactDimensionStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * DIM_FLOATS),
    floats2: new Float32Array(cap * DIM_FLOATS_2),
    ints: new Uint16Array(cap * DIM_INTS),
    types: new Uint8Array(cap),
    extLine1Points: new Map(),
    extLine2Points: new Map(),
    thirdPoints: new Map(),
    arcPoints: new Map(),
    annotativeContexts: new Map(),
    versions: new Map(),
    lineSpacingStyles: new Map(),
    lineSpacingFactors: new Map(),
    insertionPoints: new Map(),
    thicknesses: new Map(),
    extrusions: new Map(),
    textMirrored: new Set(),
  };
}

function ensureDimCap(s: CompactDimensionStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const f = new Float32Array(c * DIM_FLOATS); f.set(s.floats);
  const f2 = new Float32Array(c * DIM_FLOATS_2); f2.set(s.floats2);
  const i = new Uint16Array(c * DIM_INTS); i.set(s.ints);
  const t = new Uint8Array(c); t.set(s.types);
  s.floats = f; s.floats2 = f2; s.ints = i; s.types = t;
}

export function appendDimension(
  s: CompactDimensionStore,
  table: SharedStringTable,
  e: DimensionEntity,
): number {
  const i = s.count;
  ensureDimCap(s, i + 1);
  s.count++;
  const f = i * DIM_FLOATS;
  s.floats[f + 0] = e.defPoint.x;
  s.floats[f + 1] = e.defPoint.y;
  s.floats[f + 2] = e.textMidpoint.x;
  s.floats[f + 3] = e.textMidpoint.y;
  s.floats[f + 4] = e.textHeight;
  s.floats[f + 5] = e.textRotation;
  s.floats[f + 6] = e.measurement;
  s.floats[f + 7] = e.linearAngle;
  s.floats[f + 8] = e.obliqueAngle;
  s.floats[f + 9] = e.textAngleRelativeToDimLine;
  const f2 = i * DIM_FLOATS_2;
  s.floats2[f2 + 0] = e.leaderLength;
  const ii = i * DIM_INTS;
  s.ints[ii + 0] = internString(table, e.styleName);
  s.ints[ii + 1] = internString(table, e.dimensionText);
  s.ints[ii + 2] = internString(table, e.anonymousBlockName);
  s.ints[ii + 3] = e.attachmentPoint & 0xffff;
  s.types[i] = e.dimensionType & 0xff;
  writeBase(s.base, table, i, e);
  if (e.extLine1Point !== undefined) s.extLine1Points.set(i, { ...e.extLine1Point });
  if (e.extLine2Point !== undefined) s.extLine2Points.set(i, { ...e.extLine2Point });
  if (e.thirdPoint !== undefined) s.thirdPoints.set(i, { ...e.thirdPoint });
  if (e.arcPoint !== undefined) s.arcPoints.set(i, { ...e.arcPoint });
  if (e.annotativeContexts.length > 0) {
    s.annotativeContexts.set(i, e.annotativeContexts.map((c) => ({ ...c })));
  }
  // S-DIMENSION-EXTRAS — sparse optional fields. Mirror the optional-point
  // Maps: only store a present / non-default value so the common default
  // dimension stays Map-free and the reconstructed model matches the bridge
  // omit conventions (identity (0,0,1) extrusion + 0 thickness ⇒ absent).
  if (e.version !== undefined) s.versions.set(i, e.version);
  if (e.lineSpacingStyle !== undefined) s.lineSpacingStyles.set(i, e.lineSpacingStyle);
  if (e.lineSpacingFactor !== undefined) s.lineSpacingFactors.set(i, e.lineSpacingFactor);
  if (e.insertionPoint !== undefined) s.insertionPoints.set(i, { ...e.insertionPoint });
  if (e.thickness !== undefined && e.thickness !== 0) s.thicknesses.set(i, e.thickness);
  if (
    e.extrusion !== undefined &&
    !(e.extrusion.x === 0 && e.extrusion.y === 0 && e.extrusion.z === 1)
  ) {
    s.extrusions.set(i, { ...e.extrusion });
  }
  // GitHub Codex review PR #558 round 2 P1 — see the interface field's doc.
  if (e.textMirrored) s.textMirrored.add(i);
  return i;
}

export function dimensionFromCompact(
  s: CompactDimensionStore,
  table: SharedStringTable,
  i: number,
): DimensionEntity {
  if (i < 0 || i >= s.count) {
    throw new RangeError(
      `dimensionFromCompact: index ${i} out of range (count=${s.count})`,
    );
  }
  const f = i * DIM_FLOATS;
  const f2 = i * DIM_FLOATS_2;
  const ii = i * DIM_INTS;
  const result: DimensionEntity = {
    ...readBase(s.base, table, i),
    type: "Dimension",
    dimensionType: s.types[i]!,
    defPoint: { x: s.floats[f + 0]!, y: s.floats[f + 1]! },
    textMidpoint: { x: s.floats[f + 2]!, y: s.floats[f + 3]! },
    dimensionText: lookupString(table, s.ints[ii + 1]!),
    styleName: lookupString(table, s.ints[ii + 0]!),
    textHeight: s.floats[f + 4]!,
    textRotation: s.floats[f + 5]!,
    measurement: s.floats[f + 6]!,
    linearAngle: s.floats[f + 7]!,
    attachmentPoint: s.ints[ii + 3]!,
    obliqueAngle: s.floats[f + 8]!,
    textAngleRelativeToDimLine: s.floats[f + 9]!,
    leaderLength: s.floats2[f2 + 0]!,
    anonymousBlockName: lookupString(table, s.ints[ii + 2]!),
    annotativeContexts: s.annotativeContexts.get(i) ?? [],
    // S-DIMENSION-EXTRAS — thickness is a required model field (default 0);
    // the sparse Map below overrides it when a non-zero value was stored.
    thickness: 0,
    // Opt 2b — compact stores currently don't persist per-entity DIMVAR
    // overrides; reconstructing here yields the empty default. Adding
    // backing storage is a follow-up to keep this PR focused on the
    // schema/parser/writer plumbing.
    styleOverrides: {},
  };
  const e1 = s.extLine1Points.get(i);
  if (e1 !== undefined) result.extLine1Point = e1;
  const e2 = s.extLine2Points.get(i);
  if (e2 !== undefined) result.extLine2Point = e2;
  const tp = s.thirdPoints.get(i);
  if (tp !== undefined) result.thirdPoint = tp;
  const ap = s.arcPoints.get(i);
  if (ap !== undefined) result.arcPoint = ap;
  // S-DIMENSION-EXTRAS — restore the sparse optional fields. thickness reads
  // back as 0 (the schema default) when absent; extrusion / insertionPoint /
  // version / lineSpacing* stay absent when the Map has no entry.
  const ver = s.versions.get(i);
  if (ver !== undefined) result.version = ver;
  const lss = s.lineSpacingStyles.get(i);
  if (lss !== undefined) result.lineSpacingStyle = lss;
  const lsf = s.lineSpacingFactors.get(i);
  if (lsf !== undefined) result.lineSpacingFactor = lsf;
  const ip = s.insertionPoints.get(i);
  if (ip !== undefined) result.insertionPoint = { ...ip };
  const th = s.thicknesses.get(i);
  if (th !== undefined) result.thickness = th;
  const ext = s.extrusions.get(i);
  if (ext !== undefined) result.extrusion = { ...ext };
  // GitHub Codex review PR #558 round 2 P1 — PRESENCE-preserving (only set
  // when `true`), matching every OTHER sparse-optional field in THIS store
  // (`version`/`lineSpacingStyle`/…, all `if present then set`) rather than
  // Multileader's own always-materialized-boolean convention — a Dimension
  // that was never mirrored round-trips back to a genuinely ABSENT
  // `textMirrored` (not an explicit `false`), matching the schema's
  // `.optional()` (not `.default()`) shape and this file's own established
  // idiom exactly (`feedback-roundtrip-preserve-source-never-infer`).
  if (s.textMirrored.has(i)) result.textMirrored = true;
  return result;
}

export function estimateDimensionBytesPerEntity(): number {
  return DIM_FLOATS * 4 + DIM_FLOATS_2 * 4 + DIM_INTS * 2 + 1 + BASE_BYTES_PER_ENTITY;
}

/* ── MULTILEADER ── */

const ML_FLOATS = 12;
const ML_INTS = 6;
// Codex #286-10: blockConnectionType moved OUT of this fixed int column into the
// sparse `blockConnectionTypes` Map so absence (undefined) is preserved (a fixed
// column cannot represent "omitted" and flattened it to an invented 0). Width
// dropped 6 → 5 accordingly.
const ML_INTS_2 = 5;
const FLAG_TEXT_FRAME = 1;
const FLAG_LANDING = 2;
const FLAG_DOGLEG = 4;
// GitHub Codex review PR #558 (post-rebase fresh pass) P2 — MTEXT-content
// handedness (`MultileaderEntity.textMirrored`'s doc comment). Reuses the
// existing per-entity `flags` byte column (4 of 8 bits now used) rather than
// a new sparse Map — `.optional()` boolean collapses cleanly to a bit,
// mirroring how `textFrameDisplay`/`landingEnabled`/`doglegEnabled` already
// do (absent ⇒ bit 0 ⇒ reads back `false`, matching the model default).
const FLAG_TEXT_MIRRORED = 8;

export interface CompactMultileaderStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  ints2: Uint16Array;
  flags: Uint8Array;
  leaderRootsJson: string[];
  blockAttributeRefsJson: Map<number, string>;
  annotativeContextsJson: Map<number, string>;
  basePoints: Map<number, { x: number; y: number }>;
  extraMlVars: Map<number, Record<string, string>>;
  propertyOverrideFlags: Map<number, number>;
  // gap-C.2 — AcDbMLeader BODY group 175 (text_alignment_type). Sparse map
  // (only stored when non-zero), mirroring propertyOverrideFlags.
  textAlignmentType: Map<number, number>;
  // AcDbMLeader BODY group 176 (block_connection_type). Sparse map so ABSENCE
  // (model field `.optional()`, source omitted 176) is preserved through the
  // compact cache — a present 0 stores an entry, an omitted 176 stores none and
  // reads back undefined. A fixed int column cannot represent absence and
  // flattened it to an invented `176\n0` on the streamed/compact export path
  // (Codex #286-10; the inline-bridge fix is the model `.optional()` + writer
  // `!== undefined` guard, but streamed drawings export from this cache).
  blockConnectionTypes: Map<number, number>;
  // #294 r2 — annotative SECONDARY CONTEXT_DATA blocks. Sparse JSON map (same
  // serialization convention as annotativeContextsJson — the per-block nesting
  // is too deep for a flat column layout) keyed on PRESENCE, not length: the
  // model field is `.optional()` / absence-preserving, and streamed/large
  // drawings export from this cache, so an entry exists iff the entity carried
  // the field. Without this carry an imported annotative MLEADER read back
  // from AggregateStore lost every secondary block before export — the same
  // compact-absence disease as blockConnectionType above (Codex #286-10).
  secondaryContextsJson: Map<number, string>;
  // SL-5 P2 — AcDbMLeader BODY group 340 (MLEADERSTYLE leader-style handle).
  // Sparse map keyed on PRESENCE (`.optional()` model field): an imported
  // MLEADER's SOURCE 340 handle must survive the compact/streamed export path,
  // or the document-export stamper re-mints a DIFFERENT handle on a big-file
  // save (silent style-identity churn). A single-context authored MLEADER (no
  // handle yet) stores no entry and reads back undefined.
  leaderStyleHandles: Map<number, string>;
  // SL-5 P3a — AcDbMLeader BODY group 270 (version, ezdxf default 2). Sparse map
  // so a source-omitted 270 reads back ABSENT (the writer then defaults to 2);
  // without the carry a streamed import of a non-2 / present 270 silently
  // defaulted to 2 on export.
  versions: Map<number, number>;
  smallStrings: Uint16Array;
  spacingDistances: Map<number, number>;
  // #432 r13 (@codex R13-4) — round-8/9/11 typed MLEADER fields the compact store
  // previously DROPPED, so a streamed/large-doc MLEADER rematerialized from this
  // cache lost them before export. All sparse / PRESENCE-keyed (the model fields
  // are `.optional()`), so absence stays absence (no invented default):
  //   blockScaleVector — body 10/20/30 (non-default block scale, R11 seam)
  //   blockRecordHandle — body 344 raw BLOCK_RECORD handle (source-preserved)
  //   contextDataJson   — the whole CONTEXT_DATA record (nested → JSON, like
  //                       leaderRootsJson / secondaryContextsJson); without it the
  //                       bridge re-synthesizes a context, losing the source
  //                       MTextData / BlockData / plane / rawContextGroups fidelity
  //   rawContextGroupsJson — verbatim in-context remainder (byte-faithful)
  blockScaleVectors: Map<number, { x: number; y: number; z: number }>;
  blockRecordHandles: Map<number, string>;
  contextDataJson: Map<number, string>;
  rawContextGroupsJson: Map<number, string>;
}

export function createCompactMultileaderStore(
  initialCapacity = 128,
): CompactMultileaderStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * ML_FLOATS),
    ints: new Uint16Array(cap * ML_INTS),
    ints2: new Uint16Array(cap * ML_INTS_2),
    flags: new Uint8Array(cap),
    leaderRootsJson: new Array<string>(cap).fill(""),
    blockAttributeRefsJson: new Map(),
    annotativeContextsJson: new Map(),
    basePoints: new Map(),
    extraMlVars: new Map(),
    propertyOverrideFlags: new Map(),
    textAlignmentType: new Map(),
    blockConnectionTypes: new Map(),
    secondaryContextsJson: new Map(),
    leaderStyleHandles: new Map(),
    versions: new Map(),
    smallStrings: new Uint16Array(cap * 3),
    spacingDistances: new Map(),
    blockScaleVectors: new Map(),
    blockRecordHandles: new Map(),
    contextDataJson: new Map(),
    rawContextGroupsJson: new Map(),
  };
}

function ensureMlCap(s: CompactMultileaderStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const f = new Float32Array(c * ML_FLOATS); f.set(s.floats);
  const i = new Uint16Array(c * ML_INTS); i.set(s.ints);
  const i2 = new Uint16Array(c * ML_INTS_2); i2.set(s.ints2);
  const fl = new Uint8Array(c); fl.set(s.flags);
  const ss = new Uint16Array(c * 3); ss.set(s.smallStrings);
  s.floats = f; s.ints = i; s.ints2 = i2; s.flags = fl; s.smallStrings = ss;
  const oldLen = s.leaderRootsJson.length;
  s.leaderRootsJson.length = c;
  for (let k = oldLen; k < c; k++) s.leaderRootsJson[k] = "";
}

export function appendMultileader(
  s: CompactMultileaderStore,
  table: SharedStringTable,
  e: MultileaderEntity,
): number {
  const i = s.count;
  ensureMlCap(s, i + 1);
  s.count++;
  const f = i * ML_FLOATS;
  s.floats[f + 0] = e.textLocation.x;
  s.floats[f + 1] = e.textLocation.y;
  s.floats[f + 2] = e.textHeight;
  s.floats[f + 3] = e.textRotation;
  s.floats[f + 4] = e.textWidth;
  s.floats[f + 5] = e.arrowheadSize;
  s.floats[f + 6] = e.doglegLength;
  s.floats[f + 7] = e.landingGap;
  s.floats[f + 8] = e.breakSize;
  s.floats[f + 9] = e.blockScale;
  s.floats[f + 10] = e.blockRotation;
  s.floats[f + 11] = e.textLineSpacingFactor;

  const ii = i * ML_INTS;
  s.ints[ii + 0] = internString(table, e.styleName);
  s.ints[ii + 1] = internString(table, e.textStyleName);
  s.ints[ii + 2] = internString(table, e.textContents);
  s.ints[ii + 3] = internString(table, e.blockName);
  s.ints[ii + 4] = internString(table, e.arrowheadBlock);
  s.ints[ii + 5] = internString(table, e.leaderLineColor);

  const i2 = i * ML_INTS_2;
  s.ints2[i2 + 0] = e.textAttachmentType & 0xffff;
  s.ints2[i2 + 1] = e.textAttachmentDirection & 0xffff;
  s.ints2[i2 + 2] = e.textLineSpacingStyle & 0xffff;
  s.ints2[i2 + 3] = e.leaderLineType & 0xffff;
  s.ints2[i2 + 4] = e.contentType & 0xffff;

  let fl = 0;
  if (e.textFrameDisplay) fl |= FLAG_TEXT_FRAME;
  if (e.landingEnabled) fl |= FLAG_LANDING;
  if (e.doglegEnabled) fl |= FLAG_DOGLEG;
  if (e.textMirrored) fl |= FLAG_TEXT_MIRRORED;
  s.flags[i] = fl;

  const ss = i * 3;
  s.smallStrings[ss + 0] = internString(table, e.textColor);
  s.smallStrings[ss + 1] = internString(table, e.leaderLineWeight);
  s.smallStrings[ss + 2] = internString(table, e.blockColor);

  writeBase(s.base, table, i, e);

  s.leaderRootsJson[i] = JSON.stringify(e.leaderRoots);
  if (e.blockAttributeReferences.length > 0) {
    s.blockAttributeRefsJson.set(i, JSON.stringify(e.blockAttributeReferences));
  }
  if (e.annotativeContexts.length > 0) {
    s.annotativeContextsJson.set(i, JSON.stringify(e.annotativeContexts));
  }
  if (e.basePoint !== undefined) {
    s.basePoints.set(i, { ...e.basePoint });
  }
  if (Object.keys(e.extraMlVars).length > 0) {
    s.extraMlVars.set(i, { ...e.extraMlVars });
  }
  if (e.propertyOverrideFlags !== 0) {
    s.propertyOverrideFlags.set(i, e.propertyOverrideFlags);
  }
  if (e.textAlignmentType !== 0) {
    s.textAlignmentType.set(i, e.textAlignmentType);
  }
  // Codex #286-10 — store body 176 only when PRESENT (incl. a present 0). An
  // omitted (undefined) blockConnectionType leaves NO entry, so absence survives
  // the compact cache instead of flattening to an invented 0.
  if (e.blockConnectionType !== undefined) {
    s.blockConnectionTypes.set(i, e.blockConnectionType);
  }
  // #294 r2 — carry the annotative SECONDARY CONTEXT_DATA blocks verbatim.
  // Keyed on PRESENCE (`!== undefined`, not length) so the `.optional()` model
  // field reads back exactly as stored: absent stays absent (no invented []),
  // present — every secondary block (context header + leader hierarchy +
  // per-block raw remainder) survives the compact/streamed export path.
  if (e.secondaryContexts !== undefined) {
    s.secondaryContextsJson.set(i, JSON.stringify(e.secondaryContexts));
  }
  // SL-5 P2 — carry the source-preserved leader-style handle (340) + version
  // (270) verbatim. Keyed on PRESENCE (`!== undefined`) so the `.optional()`
  // model fields read back exactly as stored (absent stays absent): a streamed
  // import must not lose the 340 handle (→ re-mint) or the non-2 270 (→ default).
  if (e.leaderStyleHandle !== undefined) {
    s.leaderStyleHandles.set(i, e.leaderStyleHandle);
  }
  if (e.version !== undefined) {
    s.versions.set(i, e.version);
  }
  if (e.textLineSpacingDistance !== 0) {
    s.spacingDistances.set(i, e.textLineSpacingDistance);
  }
  // #432 r13 (@codex R13-4) — store the previously-dropped optional fields, keyed
  // on PRESENCE so absence survives the cache exactly (no invented default).
  if (e.blockScaleVector !== undefined) {
    s.blockScaleVectors.set(i, { ...e.blockScaleVector });
  }
  if (e.blockRecordHandle !== undefined) {
    s.blockRecordHandles.set(i, e.blockRecordHandle);
  }
  if (e.contextData !== undefined) {
    s.contextDataJson.set(i, JSON.stringify(e.contextData));
  }
  if (e.rawContextGroups !== undefined) {
    s.rawContextGroupsJson.set(i, JSON.stringify(e.rawContextGroups));
  }
  return i;
}

export function multileaderFromCompact(
  s: CompactMultileaderStore,
  table: SharedStringTable,
  i: number,
): MultileaderEntity {
  if (i < 0 || i >= s.count) {
    throw new RangeError(
      `multileaderFromCompact: index ${i} out of range (count=${s.count})`,
    );
  }
  const f = i * ML_FLOATS;
  const ii = i * ML_INTS;
  const i2 = i * ML_INTS_2;
  const fl = s.flags[i] ?? 0;
  const ss = i * 3;
  const rootsJson = s.leaderRootsJson[i] ?? "";
  let leaderRoots: MultileaderEntity["leaderRoots"];
  try {
    leaderRoots = rootsJson === "" ? [] : JSON.parse(rootsJson);
  } catch {
    leaderRoots = [];
  }
  const attrJson = s.blockAttributeRefsJson.get(i);
  let blockAttributeReferences: MultileaderEntity["blockAttributeReferences"] = [];
  if (attrJson !== undefined) {
    try { blockAttributeReferences = JSON.parse(attrJson); } catch { blockAttributeReferences = []; }
  }
  const annoJson = s.annotativeContextsJson.get(i);
  let annotativeContexts: MultileaderEntity["annotativeContexts"] = [];
  if (annoJson !== undefined) {
    try { annotativeContexts = JSON.parse(annoJson); } catch { annotativeContexts = []; }
  }
  const result: MultileaderEntity = {
    ...readBase(s.base, table, i),
    type: "Multileader",
    styleName: lookupString(table, s.ints[ii + 0]!),
    // #432 r13 (@codex R13-3) — PRESERVE an empty leaderRoots (a valid content-only
    // multileader). The old synthetic origin leader rematerialized a phantom
    // `302 LEADER{` frame to (0,0) on export — the exact phantom R11-1 removed. The
    // model schema now accepts `leaderRoots=[]`.
    leaderRoots,
    contentType: s.ints2[i2 + 4]!,
    textContents: lookupString(table, s.ints[ii + 2]!),
    textStyleName: lookupString(table, s.ints[ii + 1]!),
    textHeight: s.floats[f + 2]!,
    textRotation: s.floats[f + 3]!,
    textMirrored: (fl & FLAG_TEXT_MIRRORED) !== 0,
    textColor: lookupString(table, s.smallStrings[ss + 0]!),
    textAttachmentType: s.ints2[i2 + 0]!,
    textAttachmentDirection: s.ints2[i2 + 1]!,
    textFrameDisplay: (fl & FLAG_TEXT_FRAME) !== 0,
    textLineSpacingStyle: s.ints2[i2 + 2]!,
    textLineSpacingFactor: s.floats[f + 11]!,
    textLineSpacingDistance: s.spacingDistances.get(i) ?? 0,
    textWidth: s.floats[f + 4]!,
    arrowheadSize: s.floats[f + 5]!,
    arrowheadBlock: lookupString(table, s.ints[ii + 4]!),
    leaderLineType: s.ints2[i2 + 3]!,
    leaderLineColor: lookupString(table, s.ints[ii + 5]!),
    leaderLineWeight: lookupString(table, s.smallStrings[ss + 1]!),
    landingEnabled: (fl & FLAG_LANDING) !== 0,
    doglegEnabled: (fl & FLAG_DOGLEG) !== 0,
    doglegLength: s.floats[f + 6]!,
    landingGap: s.floats[f + 7]!,
    breakSize: s.floats[f + 8]!,
    blockName: lookupString(table, s.ints[ii + 3]!),
    blockScale: s.floats[f + 9]!,
    blockRotation: s.floats[f + 10]!,
    blockColor: lookupString(table, s.smallStrings[ss + 2]!),
    blockAttributeReferences,
    annotativeContexts,
    textLocation: { x: s.floats[f + 0]!, y: s.floats[f + 1]! },
    propertyOverrideFlags: s.propertyOverrideFlags.get(i) ?? 0,
    textAlignmentType: s.textAlignmentType.get(i) ?? 0,
    extraMlVars: s.extraMlVars.get(i) ?? {},
  };
  const bp = s.basePoints.get(i);
  if (bp !== undefined) result.basePoint = bp;
  // Codex #286-10 — restore body 176 only when an entry exists; an omitted 176
  // reads back as ABSENT (undefined) so the writer's `!== undefined` guard does
  // NOT emit an invented `176\n0` on the streamed/compact export path.
  const bct = s.blockConnectionTypes.get(i);
  if (bct !== undefined) result.blockConnectionType = bct;
  // #294 r2 — restore the secondary CONTEXT_DATA blocks only when an entry
  // exists; a single-context MLEADER reads back with the field ABSENT (not []),
  // matching the `.optional()` schema. On a corrupted payload leave the field
  // absent rather than invent an empty array (absence-preserving).
  const secJson = s.secondaryContextsJson.get(i);
  if (secJson !== undefined) {
    try {
      result.secondaryContexts = JSON.parse(
        secJson,
      ) as MultileaderEntity["secondaryContexts"];
    } catch {
      // leave absent
    }
  }
  // SL-5 P2 — restore the 340 leader-style handle + 270 version only when an
  // entry exists; an absent handle / version reads back UNDEFINED so the model's
  // `.optional()` fields (and the writer's re-mint / default-2 behaviour) match
  // the inline-bridge path exactly.
  const lsh = s.leaderStyleHandles.get(i);
  if (lsh !== undefined) result.leaderStyleHandle = lsh;
  const ver = s.versions.get(i);
  if (ver !== undefined) result.version = ver;
  // #432 r13 (@codex R13-4) — restore the previously-dropped optional fields only
  // when an entry exists (absence-preserving, matching the `.optional()` schema).
  const bsv = s.blockScaleVectors.get(i);
  if (bsv !== undefined) result.blockScaleVector = { ...bsv };
  const brh = s.blockRecordHandles.get(i);
  if (brh !== undefined) result.blockRecordHandle = brh;
  const ctxJson = s.contextDataJson.get(i);
  if (ctxJson !== undefined) {
    try {
      result.contextData = JSON.parse(
        ctxJson,
      ) as MultileaderEntity["contextData"];
    } catch {
      // leave absent
    }
  }
  const rawCtxJson = s.rawContextGroupsJson.get(i);
  if (rawCtxJson !== undefined) {
    try {
      result.rawContextGroups = JSON.parse(
        rawCtxJson,
      ) as MultileaderEntity["rawContextGroups"];
    } catch {
      // leave absent
    }
  }
  return result;
}

export function estimateMultileaderBytesPerEntity(
  s: CompactMultileaderStore,
): number {
  const fixed =
    ML_FLOATS * 4 + ML_INTS * 2 + ML_INTS_2 * 2 + 1 + 3 * 2 + BASE_BYTES_PER_ENTITY + 8;
  if (s.count === 0) return fixed;
  let jsonChars = 0;
  for (let i = 0; i < s.count; i++) jsonChars += (s.leaderRootsJson[i] ?? "").length;
  return fixed + (jsonChars * 2) / s.count;
}
